const DEFAULT_ICE_GATHER_TIMEOUT_MS = 20000;

const isMobileBrowser = () => {
    if (typeof navigator === 'undefined') return false;
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
};

export const getDefaultTurnIceServers = (): RTCIceServer[] => {
    const turnCredentials = {
        username: process.env.REACT_APP_TURN_SERVER_USER_NAME,
        credential: process.env.REACT_APP_TURN_SERVER_USER_PASSWORD,
    };

    return [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'turn:152.67.153.88:3478?transport=udp', ...turnCredentials },
        { urls: 'turn:152.67.153.88:3478?transport=tcp', ...turnCredentials },
    ];
};

export const getDefaultPeerConnectionConfig = (): RTCConfiguration => ({
    iceServers: getDefaultTurnIceServers(),
    iceTransportPolicy: 'all',
    bundlePolicy: 'balanced',
    rtcpMuxPolicy: 'require',
    iceCandidatePoolSize: 10,
});

/**
 * Waits for ICE gathering to finish. On mobile, gathering may never reach
 * "complete" before the timeout — we proceed with whatever candidates exist.
 */
export const waitForIceGatheringComplete = (
    peerConnection: RTCPeerConnection,
    options?: { timeoutMs?: number; label?: string },
): Promise<void> => {
    const timeoutMs = options?.timeoutMs ?? (isMobileBrowser() ? 25000 : DEFAULT_ICE_GATHER_TIMEOUT_MS);
    const label = options?.label ?? 'WebRTC';

    if (peerConnection.iceGatheringState === 'complete') {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        let settled = false;

        const finish = (reason: 'complete' | 'null-candidate' | 'timeout') => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            peerConnection.removeEventListener('icegatheringstatechange', onGatheringStateChange);
            peerConnection.removeEventListener('icecandidate', onIceCandidate);

            if (reason === 'timeout') {
                console.warn(
                    `[${label}] ICE gathering timed out after ${timeoutMs}ms (state: ${peerConnection.iceGatheringState}) — proceeding with current SDP`,
                );
            } else {
                console.log(`[${label}] ICE gathering finished (${reason}, state: ${peerConnection.iceGatheringState})`);
            }

            resolve();
        };

        const timeoutId = setTimeout(() => finish('timeout'), timeoutMs);

        const onGatheringStateChange = () => {
            if (peerConnection.iceGatheringState === 'complete') {
                finish('complete');
            }
        };

        const onIceCandidate = (event: RTCPeerConnectionIceEvent) => {
            if (!event.candidate) {
                finish('null-candidate');
            }
        };

        peerConnection.addEventListener('icegatheringstatechange', onGatheringStateChange);
        peerConnection.addEventListener('icecandidate', onIceCandidate);

        if (peerConnection.iceGatheringState === 'complete') {
            finish('complete');
        }
    });
};
